import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';

import {
    computePasswordStrength,
    getPasswordStrengthChecks,
    getPasswordStrengthMessage,
    mapYupValidationErrors,
    PASSWORD_SPECIAL_CHARACTER_REGEX,
    PASSWORD_SPECIAL_CHARACTERS,
} from '../../utils/passwordFormHelpers';
import ButtonComponent from '../button/ButtonComponent';
import { PassWordGuidelinesWrapper } from '../global-styles/GlobalStyles';
import { TextFieldComponent } from '../text-field';
import { colors } from '../theme/theme';

// Password validation schema
const passwordValidationSchema = Yup.object().shape({
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .max(20, 'Password must be at most 20 characters')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .matches(PASSWORD_SPECIAL_CHARACTER_REGEX, 'Password must contain at least one special character')
        .required('Password is required'),
    confirmPassword: Yup.string()
        .required('This field is empty')
        .oneOf([Yup.ref('password')], "Passwords don't match."),
});

interface PasswordSetupProps {
    onPasswordSetupComplete?: (password: string) => void;
    onBack?: () => void;
    forgotPassword?: boolean;
}

interface PasswordStrengthMeta {
    strength: number;
    level: 0 | 1 | 2 | 3;
    color: string;
    label: string;
}

// Derives strength score, level, color and label from a single strength computation,
// avoiding repeated computePasswordStrength() calls and duplicated threshold checks.
const getPasswordStrengthMeta = (password: string): PasswordStrengthMeta => {
    if (!password) {
        return { strength: 0, level: 0, color: colors.greyPreset[20], label: '' };
    }
    const strength = computePasswordStrength(password);
    if (strength <= 1) {
        return { strength, level: 1, color: colors.errorPreset[10], label: 'Too weak' };
    }
    if (strength <= 4) {
        return { strength, level: 2, color: colors.warningPreset[50], label: 'Medium' };
    }
    return { strength, level: 3, color: colors.successPreset[30], label: 'Strong' };
};

export const PasswordSetup: React.FC<PasswordSetupProps> = ({ onPasswordSetupComplete, forgotPassword }) => {
    const theme = useTheme();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
    const strengthMeta = useMemo(() => getPasswordStrengthMeta(password), [password]);
    const requirementChecks = useMemo(() => getPasswordStrengthChecks(password), [password]);

    const validatePasswords = async () => {
        try {
            await passwordValidationSchema.validate({ password, confirmPassword }, { abortEarly: false });
            setPasswordErrors({});
            return true;
        } catch (error: unknown) {
            setPasswordErrors(mapYupValidationErrors(error));
            return false;
        }
    };

    const isFormValid = () => {
        // Form is valid when:
        // 1. Both password fields have values
        // 2. Password meets all requirements
        // 3. Confirm password matches password
        // 4. No errors exist
        if (!password || !confirmPassword) {
            return false;
        }
        if (Object.keys(passwordErrors).length > 0) {
            return false;
        }
        return strengthMeta.strength === 5 && password === confirmPassword;
    };

    const handlePasswordSubmit = async () => {
        const isValid = await validatePasswords();
        if (isValid) {
            // Handle password setup completion
            if (onPasswordSetupComplete) {
                onPasswordSetupComplete(password);
            } else {
                alert('Registration completed successfully! Welcome to Auxilior Capital Partners.');
            }
        }
    };

    return (
        <Stack
            direction={{ sx: 'column', sm: 'row' }}
            sx={{
                width: '100%',
                gap: 3,
            }}
        >
            {/* Left Side - Form */}
            <Box sx={{ width: '100%' }} maxWidth={{ xs: '100%', sm: '300px' }}>
                {/* Password Setup Title */}
                <Typography
                    variant="h5"
                    className="mainTitle"
                    component="h1"
                    sx={{ mb: '8px !important', textAlign: 'start !important' }}
                >
                    {forgotPassword ? 'Reset your password' : 'Password Setup'}
                </Typography>

                {/* Subtitle */}
                <Typography variant="body2" mb={3} sx={{ color: colors.greyPreset[80] }}>
                    {"Enter a new password below. Make sure it's strong and something you haven't used before."}
                </Typography>

                {/* New Password Field */}
                <Box sx={{ mb: 2, textAlign: 'left' }}>
                    <TextFieldComponent
                        label="New Password"
                        type="password"
                        value={password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            if (value.length <= 20) {
                                setPassword(value);
                                if (passwordErrors.password) {
                                    setPasswordErrors((prev: Record<string, string>) => ({ ...prev, password: '' }));
                                }
                                if (confirmPassword && value !== confirmPassword) {
                                    setPasswordErrors((prev: Record<string, string>) => ({
                                        ...prev,
                                        confirmPassword: "Passwords don't match.",
                                    }));
                                } else if (confirmPassword && value === confirmPassword) {
                                    setPasswordErrors((prev: Record<string, string>) => {
                                        const updated = { ...prev };
                                        delete updated.confirmPassword;
                                        return updated;
                                    });
                                }
                            }
                        }}
                        variant="outlined"
                        required
                        maxLength={20}
                        error={!!passwordErrors.password}
                        errorValue={passwordErrors.password}
                    />
                </Box>

                {/* Confirm Password Field */}
                <Box sx={{ mb: 3, textAlign: 'left' }}>
                    <TextFieldComponent
                        label="Confirm New Password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            // Limit to 20 characters
                            if (value.length <= 20) {
                                setConfirmPassword(value);
                                // Display real-time error if passwords don't match (only when confirm password has a value)
                                if (password && value && value !== password) {
                                    setPasswordErrors((prev: Record<string, string>) => ({
                                        ...prev,
                                        confirmPassword: "Passwords don't match.",
                                    }));
                                } else if (value && password && value === password) {
                                    // Clear error when passwords match
                                    setPasswordErrors((prev: Record<string, string>) => {
                                        const updated = { ...prev };
                                        delete updated.confirmPassword;
                                        return updated;
                                    });
                                } else if (!value) {
                                    // Clear error when confirm password is emptied
                                    setPasswordErrors((prev: Record<string, string>) => {
                                        const updated = { ...prev };
                                        delete updated.confirmPassword;
                                        return updated;
                                    });
                                }
                            }
                        }}
                        variant="outlined"
                        required
                        maxLength={20}
                        error={!!passwordErrors.confirmPassword}
                        errorValue={passwordErrors.confirmPassword}
                    />
                </Box>

                {/* Set Password Button */}
                <ButtonComponent variant="contained" onClick={handlePasswordSubmit} disabled={!isFormValid()}>
                    {forgotPassword ? 'Reset your password' : 'Set Password'}
                </ButtonComponent>
            </Box>

            {/* Right Side - Password Guidelines */}
            <PassWordGuidelinesWrapper tabIndex={0}>
                <Stack width={'100%'}>
                    <Stack direction={'row'} alignItems={'center'} gap={2} mb={2}>
                        <Avatar sx={{ bgcolor: colors.bluePreset[50] }}>
                            <span className="icon-tip" style={{ color: colors.bluePreset[30], fontSize: 24 }} />
                        </Avatar>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{ textAlign: 'start', color: theme.palette.text.primary }}
                        >
                            Password Guidelines
                        </Typography>
                    </Stack>
                    {/* Guidelines List */}
                    <Typography variant="caption" mb={1} color="textSecondary">
                        A password must be between 8-20 characters long, include at least one lowercase letter, at least
                        one uppercase letter, at least one number, and at least one special character
                    </Typography>
                    <Box>
                        <Typography
                            variant="subtitle1"
                            component="h3"
                            mb={1}
                            sx={{ textAlign: 'start', color: theme.palette.text.primary }}
                        >
                            How to meet the requirements
                        </Typography>
                        <Stack sx={{ textAlign: 'start' }} gap={1}>
                            {[
                                {
                                    label: 'Length',
                                    checkIndex: 0,
                                    description: 'Your password must be between 8 and 20 characters.',
                                },
                                {
                                    label: 'Uppercase letter',
                                    checkIndex: 2,
                                    description: 'Must include at least one uppercase letter (e.g., A, B, C).',
                                },
                                {
                                    label: 'Lowercase letter',
                                    checkIndex: 1,
                                    description: 'Must include at least one lowercase letter (e.g., a, b, c).',
                                },
                                {
                                    label: 'Number',
                                    checkIndex: 3,
                                    description: 'Must include at least one numerical digit (e.g., 1, 2, 3).',
                                },
                                {
                                    label: 'Special character',
                                    checkIndex: 4,
                                    description: (
                                        <>
                                            Must include at least one special character. Allowed characters:
                                            <Box
                                                component="span"
                                                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}
                                            >
                                                {PASSWORD_SPECIAL_CHARACTERS.map(character => (
                                                    <Box
                                                        key={character}
                                                        component="span"
                                                        sx={{
                                                            minWidth: 22,
                                                            px: 0.5,
                                                            textAlign: 'center',
                                                            fontFamily: 'monospace',
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                            lineHeight: '22px',
                                                            color: colors.bluePreset[10],
                                                            backgroundColor: colors.bluePreset[50],
                                                            border: `1px solid ${colors.bluePreset[60]}`,
                                                            borderRadius: '6px',
                                                        }}
                                                    >
                                                        {character}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    ),
                                },
                            ].map(requirement => {
                                const met = requirementChecks[requirement.checkIndex];
                                return (
                                    <Box
                                        key={requirement.label}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.25,
                                            p: 1.25,
                                            borderRadius: 2,
                                            backgroundColor: met ? colors.successPreset[40] : colors.fullWhite,
                                            border: `1px solid ${met ? colors.successPreset[20] : colors.greyPreset[20]}`,
                                            transition: 'background-color 0.2s ease, border-color 0.2s ease',
                                        }}
                                    >
                                        {met ? (
                                            <CheckCircleIcon
                                                data-testid="requirement-met"
                                                sx={{ fontSize: 20, mt: '1px', color: colors.successPreset[10] }}
                                            />
                                        ) : (
                                            <RadioButtonUncheckedIcon
                                                sx={{ fontSize: 20, mt: '1px', color: colors.greyPreset[40] }}
                                            />
                                        )}
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                component="span"
                                                sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                                            >
                                                {requirement.label}:{' '}
                                            </Typography>
                                            <Typography variant="caption" component="span" color="textSecondary">
                                                {requirement.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                    <Box sx={{ mt: 3 }}>
                        <Typography
                            variant="subtitle1"
                            mb={1}
                            component="h4"
                            sx={{ textAlign: 'start', color: theme.palette.text.primary }}
                        >
                            Password Strength
                        </Typography>
                        <Box
                            sx={{
                                width: '100%',
                                height: 6,
                                backgroundColor: colors.greyPreset[20],
                                borderRadius: 1,
                                overflow: 'hidden',
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${(strengthMeta.strength / 5) * 100}%`,
                                    height: '100%',
                                    backgroundColor: strengthMeta.color,
                                    transition: 'width 0.2s ease, background-color 0.2s ease',
                                }}
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            {getPasswordStrengthMessage(password)}
                        </Typography>
                    </Box>
                </Stack>
            </PassWordGuidelinesWrapper>
        </Stack>
    );
};
